/**
 * shoppingStore — Zustand UI-only state for the shopping route (Plan 04-03).
 *
 * Server data lives in TanStack Query (`['shopping','items']`); this store
 * owns the small set of UI bits that survive component remounts:
 *
 *   - `checkedSectionExpanded` — whether the "Haket av (N)" group is expanded.
 *     UI-SPEC §"Interaction Model — Haket av collapse/expand" locks default
 *     state = collapsed. The state persists across idle return on the tablet
 *     because Zustand lives outside React's component tree.
 *
 *   - `lastConflict` — one-shot toast slot for D-10 conflict reconciliation.
 *     `useShoppingMutations.onError(409)` writes here when the local edit was
 *     non-trivial (>3 chars per D-10) or a delete-vs-edit collision happened;
 *     `<ConflictToast />` reads, displays for 4.5s, then clears via
 *     `clearConflict()` (auto-dismiss timer).
 *
 * D-10 explicit: NEVER show a toast for benign idempotent reconciliations
 * (A and B both checked the same row). The mutation handler is responsible
 * for filtering before writing here.
 */
import { create } from 'zustand';

export type ShoppingConflictKind = 'edit' | 'removed';

export interface ShoppingConflict {
  kind: ShoppingConflictKind;
  /** Display nick of the family member whose write won the race. */
  nick: string;
}

interface ShoppingStore {
  checkedSectionExpanded: boolean;
  toggleCheckedSection: () => void;
  setCheckedSectionExpanded: (v: boolean) => void;

  lastConflict: ShoppingConflict | null;
  setConflict: (c: ShoppingConflict) => void;
  clearConflict: () => void;
}

export const useShoppingStore = create<ShoppingStore>((set) => ({
  // UI-SPEC §"Interaction Model — Haket av collapse/expand": collapsed by default.
  checkedSectionExpanded: false,
  toggleCheckedSection: () =>
    set((s) => ({ checkedSectionExpanded: !s.checkedSectionExpanded })),
  setCheckedSectionExpanded: (v) => set({ checkedSectionExpanded: v }),

  lastConflict: null,
  setConflict: (c) => set({ lastConflict: c }),
  clearConflict: () => set({ lastConflict: null }),
}));
